/* Megamenu open/close controller — vanilla port of v3.0's jQuery
 * inline handler (hover branch) plus the four-words-2.0 click branch.
 *
 * Each megamenu instance carries:
 *   data-megamenu                   marker attribute
 *   data-id="{menu-handle}"         matches a navbar trigger
 *   data-trigger-behaviour="hover"  or "click"
 *
 * Each navbar trigger (rendered by the navbar-menu snippet when its
 * link handle is in the section's megamenu_triggers list) carries:
 *   data-trigger="megamenu-{handle}"
 *
 * Two behaviours, picked per-trigger from the matching megamenu's
 * data-trigger-behaviour:
 *
 *   Hover  mouseenter / focus on the trigger opens the megamenu.
 *          mouseleave on either the trigger or the megamenu starts
 *          a 300ms close timer. Moving from the trigger into the
 *          megamenu (or vice versa) cancels the timer.
 *
 *   Click  click on the trigger opens the megamenu and prevents
 *          the link's default navigation. Re-clicking the same
 *          trigger keeps the megamenu open. The only way to close
 *          is to click outside the megamenu's content container
 *          (the megamenu's ::before pseudo-element fills the rest
 *          of the viewport and catches those clicks).
 *
 * The click-outside-container escape hatch is bound for both modes
 * so the merchant always has a quick close path.
 *
 * Body class:
 *   navbar--show-megamenu  added while any megamenu is open. Used
 *                           by component-megamenu.css to lock page
 *                           scroll (overflow: hidden).
 *
 * Trigger active class:
 *   .active is toggled on the currently-open trigger so its style
 *   can reflect the open state. */

const CLOSE_DELAY_MS = 300;

class MegamenuController {
  constructor() {
    this.megamenus = new Map(); // handle -> { el, behaviour }
    this.closeTimer = null;
  }

  init() {
    document.querySelectorAll('[data-megamenu][data-id]').forEach((el) => {
      const id = el.dataset.id;
      if (!id) return;
      const behaviour = el.dataset.triggerBehaviour === 'click' ? 'click' : 'hover';
      this.megamenus.set(id, { el, behaviour });
    });
    if (this.megamenus.size === 0) return;

    this.bindTriggers();
    this.bindMegamenus();
  }

  bindTriggers() {
    document.querySelectorAll('[data-trigger]').forEach((trigger) => {
      const name = trigger.dataset.trigger;
      if (!name || !name.startsWith('megamenu-')) return;
      const handle = name.replace('megamenu-', '');
      const entry = this.megamenus.get(handle);
      if (!entry) return;

      if (entry.behaviour === 'click') {
        trigger.addEventListener('click', (e) => {
          // Below the megamenu's lg breakpoint (992px) the megamenu
          // is `display: none` — don't intercept the link click,
          // just let the navbar trigger navigate to its href.
          // Mobile-side navigation is handled by the mobile menu
          // drawer that lands in the header port's Stage 6.
          if (window.innerWidth < 992) return;
          e.preventDefault();
          this.open(handle, trigger);
        });
      } else {
        const onOpen = () => {
          this.cancelClose();
          this.open(handle, trigger);
        };
        trigger.addEventListener('mouseenter', onOpen);
        trigger.addEventListener('focus', onOpen);
        trigger.addEventListener('mouseleave', () => this.scheduleClose());
      }
    });
  }

  bindMegamenus() {
    this.megamenus.forEach(({ el, behaviour }) => {
      // Overlay click closes — works for both modes. Provides an
      // immediate escape hatch even in hover mode.
      el.addEventListener('click', (e) => {
        if (!e.target.closest('.navbar-megamenu__container')) {
          this.closeAll();
        }
      });

      // Hover-mode tolerance scoped to the .navbar-megamenu__container
      // (not the whole megamenu). The overlay area outside the
      // container counts as "outside the menu" -- moving the cursor
      // onto it triggers a mouseleave on the container and schedules
      // a close, matching the v3.0 behaviour.
      if (behaviour === 'hover') {
        const container = el.querySelector('.navbar-megamenu__container');
        if (container) {
          container.addEventListener('mouseenter', () => this.cancelClose());
          container.addEventListener('mouseleave', () => this.scheduleClose());
        }
      }
    });
  }

  open(handle, trigger) {
    document.body.classList.add('navbar--show-megamenu');

    document
      .querySelectorAll('[data-trigger]')
      .forEach((t) => t.classList.remove('active'));
    this.megamenus.forEach(({ el }) => this._deactivateMegamenu(el));

    const entry = this.megamenus.get(handle);
    if (entry) entry.el.classList.add('is-active');
    if (trigger) trigger.classList.add('active');
  }

  // Remove .is-active from a megamenu and fire `megamenu:closed` on
  // it iff it actually was active. Linklists inside listen for this
  // event so they can reset their own link / panel state — keeping
  // a clean slate across open → close → reopen and across
  // open → switch-to-another-megamenu.
  _deactivateMegamenu(el) {
    const wasActive = el.classList.contains('is-active');
    el.classList.remove('is-active');
    if (wasActive) {
      el.dispatchEvent(new CustomEvent('megamenu:closed'));
    }
  }

  scheduleClose() {
    this.cancelClose();
    this.closeTimer = window.setTimeout(() => this.closeAll(), CLOSE_DELAY_MS);
  }

  cancelClose() {
    if (this.closeTimer) {
      window.clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }
  }

  closeAll() {
    document.body.classList.remove('navbar--show-megamenu');
    this.megamenus.forEach(({ el }) => this._deactivateMegamenu(el));
    document
      .querySelectorAll('[data-trigger]')
      .forEach((t) => t.classList.remove('active'));
    this.cancelClose();
  }
}

function start() {
  new MegamenuController().init();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', start);
} else {
  start();
}
